// ============================================================
// ReconferirRosto.js — o atleta tira outra foto quando a primeira não
// conferiu com a foto de referência
//
// A primeira foto que não confere não vira registro (27/09/2026): o atleta
// recebe este cartão e a segunda tentativa entra de qualquer jeito, com
// aviso para a coordenação. Ninguém fica sem presença por causa disto; o
// que muda é que a foto ruim é corrigida na hora, e não depois.
//
// SÓ CÂMERA, nunca arquivo. Um arquivo pode ser de qualquer dia, e num
// cartão que existe justamente para provar a presença DE HOJE isso não é
// conveniência, é o buraco. O envio de arquivo continua onde faz sentido:
// no cadastro da foto de referência, que a coordenação faz, e no
// comprovante da justificativa, que é um documento e não uma selfie.
// ============================================================

import { useState, useRef, useEffect, useCallback } from "react";
import { reconferirRosto } from "../firebase";
import { TEXTO, CINZA_ESC, BRANCO, BORDA, ATENCAO, ATENCAO_CL, OK, OK_CL } from "../tema";
import Card from "./Card";
import Btn from "./Btn";

// O que dizer depois de uma tentativa que não conferiu.
//
// Separado da tela porque são três situações diferentes que se parecem na
// pressa: serviço fora do ar não é recusa, recusa com tentativa restante
// não é fim, e fim não é culpa. Dizer a errada é pior do que não dizer.
export function mensagemDoResultado(r) {
  if (!r || r.ok) return null;
  if (r.indisponivel) return r.mensagem || "Não foi possível conferir agora.";

  const quanto = typeof r.semelhanca === "number"
    ? ` (${r.semelhanca}%, mínimo de ${r.minimo}%)`
    : "";
  const depois = r.tentativasRestantes > 0
    ? "Você ainda pode tentar mais uma vez."
    : "A coordenação vai analisar este registro.";
  return `A nova foto também não conferiu${quanto}. ${depois}`;
}

function fmtData(iso) {
  const p = String(iso || "").split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : String(iso || "");
}

export default function ReconferirRosto({ registro, aoResolver }) {
  const [camera, setCamera] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const pararCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (!camera) { pararCamera(); return undefined; }
    let cancelado = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 } }, audio: false,
        });
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => null);
        }
      } catch {
        setErro("Não foi possível abrir a câmera. Permita o acesso no navegador.");
        setCamera(false);
      }
    })();
    return () => { cancelado = true; pararCamera(); };
  }, [camera, pararCamera]);

  useEffect(() => pararCamera, [pararCamera]);

  async function enviar(dataUrl) {
    setEnviando(true);
    setErro(null);
    try {
      const r = await reconferirRosto({
        registroId: registro.id,
        fotoBase64: dataUrl,
        tipoArquivo: "image/jpeg",
      });
      setResultado(r);
      setCamera(false);
      if (r && r.ok && aoResolver) aoResolver();
    } catch (e) {
      setErro(e && e.message ? e.message : "Não foi possível enviar a foto.");
    } finally {
      setEnviando(false);
    }
  }

  function capturar() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setErro("A câmera ainda está carregando. Aguarde um instante.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    enviar(canvas.toDataURL("image/jpeg", 0.9));
  }

  if (resultado && resultado.ok) {
    return (
      <Card style={{ borderLeft: `5px solid ${OK}`, background: OK_CL }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: OK }}>Agora conferiu</div>
        <div style={{ fontSize: 13, color: TEXTO, marginTop: 6, lineHeight: 1.6 }}>
          A nova foto bateu com a sua foto de referência. A coordenação não precisa mais
          analisar este registro.
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ borderLeft: `5px solid ${ATENCAO}`, background: ATENCAO_CL }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: TEXTO }}>
        Confirme sua presença de {fmtData(registro.dateKey)}
      </div>
      {/* O texto é o combinado com a coordenação, palavra por palavra. Não
          fala em semelhança nem em porcentagem: quem lê precisa saber o que
          fazer, e "identidade não confirmada" é o que a situação é. */}
      <div style={{ fontSize: 13, color: TEXTO, marginTop: 6, lineHeight: 1.65 }}>
        Não conseguimos confirmar sua identidade pela foto enviada. Tire uma nova foto de
        frente, com o rosto bem iluminado, sem boné. Evite luz forte atrás de você.
      </div>

      {resultado && !resultado.ok && (
        <div style={{ fontSize: 13, color: "#92400E", marginTop: 10, lineHeight: 1.6, fontWeight: 600 }}>
          {mensagemDoResultado(resultado)}
        </div>
      )}

      {erro && (
        <div style={{ fontSize: 13, color: "#B91C1C", marginTop: 10, lineHeight: 1.6 }}>{erro}</div>
      )}

      {camera ? (
        <div style={{ marginTop: 12 }}>
          <video ref={videoRef} playsInline muted
            style={{ width: "100%", maxWidth: 380, borderRadius: 12, background: "#000", transform: "scaleX(-1)" }} />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <Btn onClick={capturar} disabled={enviando} icone={enviando ? undefined : "rosto"}>
              {enviando ? "Conferindo…" : "Tirar e confirmar"}
            </Btn>
            <button onClick={() => setCamera(false)} disabled={enviando}
              style={{ background: BRANCO, border: `1px solid ${BORDA}`, borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, color: TEXTO, cursor: "pointer", fontFamily: "inherit" }}>
              Agora não
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
          <Btn onClick={() => { setCamera(true); setErro(null); }} disabled={enviando} icone="rosto">
            Tirar nova foto
          </Btn>
          <span style={{ fontSize: 12, color: CINZA_ESC }}>
            {enviando ? "Conferindo…" : "A foto é tirada agora e usada só para conferir; não fica guardada."}
          </span>
        </div>
      )}
    </Card>
  );
}
